import { IAssemblyData } from '@/editor/data/assembly/assembly';
import AssemblyEntity from '@/editor/entities/assemblyEntity';
import { OperateSelectedBoxes } from '@/editor/controller/helper/operateSelectedBoxes';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { AssemblyAbsorbManager } from '@/editor/manager/absorb/box/assemblyAbsorbManager';
import { TGlobalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { moveAssembly } from '@/editor/controller/utils';
import { VirtualAssemblyEntities } from '@/editor/entities/virtualModelEntities';
import { OperateAbstract } from './operateAbstract';
import { cloneDeep } from 'lodash-es';

export class OperateAssembly extends OperateAbstract<EEntityType.Assembly> {
  declare checkedEntity: AssemblyEntity | null | undefined;

  private _assemblyAbsorbManager = new AssemblyAbsorbManager();
  private _selectedBoxes = new OperateSelectedBoxes();

  private _virtualAssemblyData: IAssemblyData | null = null;
  private _movedAssemblyData: IAssemblyData | null = null;
  private _virtualEntities = new VirtualAssemblyEntities();

  private _clickedOffset: IPoint2d = { x: 0, y: 0 };

  constructor(_gs: TGlobalStores) {
    super(_gs);
  }

  override startMove(point: IPoint2d) {
    if (!this.checkedEntity) return false;

    this.moving = false;

    const assemblyData = this._gs.getDataByIdType(this.checkedEntity.id, EEntityType.Assembly);
    if (!assemblyData) return false;

    this._clickedOffset = Point2dUtils.subtracted(assemblyData.center, point);
    this._assemblyAbsorbManager.updateData({ excludeAssemblyIds: [this.checkedEntity.id] });

    this.checkedEntity.setVisible(false);
    this._virtualAssemblyData = cloneDeep(assemblyData);
    this._virtualEntities.update(this._virtualAssemblyData, this._virtualAssemblyData);

    this.updateBoxes();

    return true;
  }

  move(point: IPoint2d) {
    if (!this.checkedEntity || !this._virtualAssemblyData) return;

    this.moving = true;

    let center = Point2dUtils.added(point, this._clickedOffset);
    let rotation = this._virtualAssemblyData.rotation;

    const absorbResult = this._assemblyAbsorbManager.calculateOnly({
      center,
      width: this._virtualAssemblyData.width,
      thickness: this._virtualAssemblyData.thickness,
      rotation
    });
    if (absorbResult) {
      center = absorbResult.absorbPoint;
      rotation = absorbResult.rotation;
    }

    this.updateMovedAssembly(center, rotation);
  }

  override moveByOffset(offset: IPoint2d) {
    if (!this.checkedEntity || !this._virtualAssemblyData) return;

    this.moving = true;

    // 累积在移动后的数据上，_virtualAssemblyData 要保持原始位置
    const base = this._movedAssemblyData ?? this._virtualAssemblyData;
    this.updateMovedAssembly(Point2dUtils.added(base.center, offset), base.rotation);
  }

  private updateMovedAssembly(center: IPoint2d, rotation: number) {
    if (!this._virtualAssemblyData) return;

    this._movedAssemblyData = moveAssembly(center, { ...this._virtualAssemblyData, rotation });
    this._virtualEntities.update(this._virtualAssemblyData, this._movedAssemblyData);
    this.updateBoxes({ x: center.x, y: center.y, rotation });
  }

  completeMove(_point: IPoint2d) {
    if (this.moving && this.checkedEntity && this._movedAssemblyData) {
      this._gs.assemblyOperation.moveAssembly(
        this.checkedEntity.id,
        this._movedAssemblyData.center,
        this._movedAssemblyData.rotation
      );
    }

    this.moving = false;
    this.checkedEntity?.setVisible(true);
    this._virtualAssemblyData = null;
    this._movedAssemblyData = null;
  }

  clear() {
    this.checkedEntity?.setVisible(true);
    this._assemblyAbsorbManager.clearModel();
    this._selectedBoxes.clear();
    this._virtualEntities.dispose();
    this._virtualAssemblyData = null;
    this._movedAssemblyData = null;
    this.checkedEntity = null;
    this.moving = false;
    this._clickedOffset = { x: 0, y: 0 };
  }

  dispose() {
    this.clear();
    this._assemblyAbsorbManager.dispose();
    this._selectedBoxes.dispose();
  }

  private updateBoxes(params?: { x: number; y: number; rotation: number }) {
    if (!this.checkedEntity) return;

    this._selectedBoxes.update({ type: EEntityType.Assembly, entityId: this.checkedEntity.id }, params);
  }
}
